import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, KeyRound, RefreshCw, AlertTriangle, Monitor, Trash2 } from "lucide-react";

/**
 * Two-factor authentication section for /account/settings.
 * - Customers: optional, can enroll & disable
 * - Staff: must enroll, cannot disable (server enforces)
 */
export default function TwoFactorSettings() {
  const { user, refreshMe } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { secret, otpauth_uri, qr_png_base64 }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once after verify
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const enabled = !!user?.totp_enabled;
  const mustEnroll = !!user?.must_enroll_2fa;

  // Auto-start enrollment when arriving from the login redirect with
  // ?enroll=1 (the LoginPage routes staff with must_enroll_2fa here so they
  // see the QR code immediately instead of being stranded with 403s).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("enroll") === "1" && mustEnroll && !enabled && !enrollData) {
      startEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustEnroll, enabled]);

  const startEnroll = async () => {
    setEnrolling(true);
    try {
      const r = await api.post("/auth/2fa/enroll");
      setEnrollData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start enrollment");
    } finally { setEnrolling(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", { code });
      setRecoveryCodes(r.data.recovery_codes);
      setEnrollData(null);
      setCode("");
      await refreshMe();
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      setDisableOpen(false);
      setDisableCode("");
      await refreshMe();
      toast.success("Two-factor authentication disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"))
      .then(() => toast.success("Recovery codes copied"))
      .catch(() => toast.error("Copy failed — please save them manually"));
  };

  // ---------- Trusted devices ----------
  const [devices, setDevices] = useState([]);
  const loadDevices = () => {
    if (!enabled) return setDevices([]);
    api.get("/auth/2fa/devices")
      .then((r) => setDevices(r.data.devices || []))
      .catch(() => {});
  };
  useEffect(() => { loadDevices(); /* eslint-disable-next-line */ }, [enabled]);
  const revokeDevice = async (deviceId) => {
    try {
      await api.post(`/auth/2fa/devices/${deviceId}/revoke`);
      toast.success("Device removed");
      loadDevices();
    } catch (e) { toast.error("Failed"); }
  };
  const revokeAll = async () => {
    if (!window.confirm("Revoke all remembered devices? You'll need to enter 2FA on every browser on next sign-in.")) return;
    try {
      await api.post("/auth/2fa/devices/revoke-all");
      localStorage.removeItem("xch_device_id");
      toast.success("All devices removed");
      loadDevices();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <section className="xch-card p-5 md:col-span-2" data-testid="twofa-section">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="font-display text-base font-bold text-foreground flex items-center gap-2">
            Two-factor authentication
            {enabled ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px] uppercase font-bold">
                <ShieldCheck className="w-3 h-3 mr-1" /> Enabled
              </Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-600 border-slate-200 border text-[10px] uppercase font-bold">
                <ShieldOff className="w-3 h-3 mr-1" /> Off
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Protect your account with an authenticator app (Google Authenticator, Authy, 1Password, etc.).
          </p>
        </div>
        {!enabled && !enrollData && (
          <Button onClick={startEnroll} disabled={enrolling}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="twofa-enroll-btn">
            <ShieldCheck className="w-4 h-4" /> {enrolling ? "Starting…" : "Set up 2FA"}
          </Button>
        )}
        {enabled && (
          <Button onClick={() => setDisableOpen(true)} variant="outline"
                  className="rounded-xl text-destructive border-destructive/40 hover:bg-destructive/5 gap-1.5"
                  data-testid="twofa-disable-btn">
            <ShieldOff className="w-4 h-4" /> Disable
          </Button>
        )}
      </div>

      {mustEnroll && !enrollData && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2"
             data-testid="twofa-staff-required-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Staff account — 2FA required.</strong> You'll be asked for a TOTP code on every sign-in once enabled.
            Customer-facing accounts will not see this notice.
          </div>
        </div>
      )}

      {enrollData && !enabled && (
        <div className="space-y-3" data-testid="twofa-enroll-flow">
          <div className="grid sm:grid-cols-2 gap-4 items-center">
            <div className="text-center">
              <img src={enrollData.qr_png_base64} alt="2FA QR code"
                   className="mx-auto w-44 h-44 rounded-xl border border-border bg-white p-2"
                   data-testid="twofa-qr-image" />
              <p className="text-[10px] text-muted-foreground mt-2">
                Or enter this secret manually
              </p>
              <div className="font-mono text-[11px] mt-1 bg-muted/40 rounded-lg p-2 break-all" data-testid="twofa-secret">
                {enrollData.secret}
              </div>
            </div>
            <form onSubmit={verify} className="space-y-2.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Enter the 6-digit code from your app
              </Label>
              <Input value={code}
                     onChange={(e) => setCode(e.target.value.trim())}
                     placeholder="123456" maxLength={6} inputMode="numeric"
                     className="rounded-xl text-center text-xl font-mono tracking-[0.4em] h-14"
                     data-testid="twofa-verify-input" />
              <Button type="submit" disabled={busy || code.length !== 6}
                      className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl"
                      data-testid="twofa-verify-btn">
                {busy ? "Verifying…" : "Verify and enable"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setEnrollData(null); setCode(""); }}
                      className="w-full text-xs">
                Cancel
              </Button>
            </form>
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div className="space-y-2 mt-3" data-testid="twofa-recovery-display">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <strong>Save these recovery codes now.</strong> They are shown only once. Each code can be used to sign in if you lose your authenticator.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs bg-muted/40 rounded-xl p-3">
            {recoveryCodes.map((c) => (
              <div key={c} className="bg-white border border-border rounded-lg px-2 py-1.5 text-center">{c}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={copyCodes} size="sm" variant="outline" className="rounded-xl gap-1.5"
                    data-testid="twofa-copy-codes-btn">
              <Copy className="w-3.5 h-3.5" /> Copy all
            </Button>
            <Button onClick={() => setRecoveryCodes(null)} size="sm" variant="ghost" className="rounded-xl text-xs"
                    data-testid="twofa-done-codes-btn">
              I've saved them
            </Button>
          </div>
        </div>
      )}

      {enabled && devices.length > 0 && (
        <div className="mt-5 pt-5 border-t border-border space-y-2.5" data-testid="twofa-devices-section">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-display font-bold text-foreground flex items-center gap-1.5">
                <Monitor className="w-4 h-4" /> Remembered devices
              </div>
              <div className="text-[10px] text-muted-foreground">
                Browsers that skip 2FA for 30 days after a successful login.
              </div>
            </div>
            <Button onClick={revokeAll} size="sm" variant="outline"
                    className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/5 text-xs h-7"
                    data-testid="twofa-revoke-all-btn">
              Remove all
            </Button>
          </div>
          <div className="space-y-1.5">
            {devices.map((d) => (
              <div key={d.device_id} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2.5"
                   data-testid={`twofa-device-${d.device_id_short}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-foreground truncate">
                    {d.label || (d.user_agent ? d.user_agent.split(" ")[0] : "Unknown browser")}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate font-mono">
                    {d.device_id_short}… · last seen {new Date(d.last_seen_at).toLocaleString("en-IE")}
                  </div>
                </div>
                <Button onClick={() => revokeDevice(d.device_id)} size="icon" variant="ghost"
                        className="h-7 w-7 text-destructive hover:bg-destructive/10 shrink-0"
                        data-testid={`twofa-revoke-${d.device_id_short}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Disable 2FA?</DialogTitle>
            <DialogDescription className="text-xs">
              Confirm with a current code from your authenticator. Your trusted-device list will be cleared.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirm by entering your current 6-digit authenticator code. Your account will become less secure.
          </p>
          <Input value={disableCode}
                 onChange={(e) => setDisableCode(e.target.value.trim())}
                 placeholder="123456" maxLength={6} inputMode="numeric"
                 className="rounded-xl text-center text-xl font-mono tracking-[0.4em] h-14"
                 data-testid="twofa-disable-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDisableOpen(false); setDisableCode(""); }}>Cancel</Button>
            <Button onClick={disable} disabled={busy || disableCode.length !== 6}
                    className="bg-destructive hover:bg-destructive/90 text-white rounded-xl"
                    data-testid="twofa-confirm-disable-btn">
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
